import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useScreenSize } from '../../../sharedComponents/src/hooks/useScreenSize'

const MAX_COLLAPSED_LENGTH_MOBILE = 95
const MAX_COLLAPSED_LENGTH_DESKTOP = 120
const MAX_TOTAL_LENGTH = 315

type BubbleContentProps = {
    message: string
}

const BubbleContent: React.FC<BubbleContentProps> = ({ message }) => {
    const [isExpanded, setIsExpanded] = useState(false)
    const bubbleRef = useRef<HTMLDivElement>(null)
    const { isMobile } = useScreenSize()
    const MAX_COLLAPSED_LENGTH = isMobile ? MAX_COLLAPSED_LENGTH_MOBILE : MAX_COLLAPSED_LENGTH_DESKTOP

    // Truncate message to max total length
    const truncatedMessage = useMemo(
        () => (message.length > MAX_TOTAL_LENGTH ? message.slice(0, MAX_TOTAL_LENGTH) : message),
        [message],
    )

    // Check if message needs truncation based on character count
    const isTruncated = useMemo(() => truncatedMessage.length > MAX_COLLAPSED_LENGTH, [truncatedMessage])

    // Display message based on expansion state
    const displayMessage = useMemo(
        () => (isExpanded || !isTruncated ? truncatedMessage : truncatedMessage.slice(0, MAX_COLLAPSED_LENGTH)),
        [isExpanded, isTruncated, truncatedMessage],
    )

    const handleBubble = useCallback(() => {
        if (isTruncated) {
            setIsExpanded(!isExpanded)
        }
    }, [isExpanded, isTruncated])

    // Click outside handler to collapse the bubble
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (bubbleRef.current && !bubbleRef.current.contains(event.target as Node)) {
                setIsExpanded(false)
            }
        }

        if (isExpanded) {
            document.addEventListener('mousedown', handleClickOutside)
            return () => document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isExpanded])

    // Collapse when message changes to support smooth transitions
    useEffect(() => {
        setIsExpanded(false)
    }, [message])

    return (
        <Stack ref={bubbleRef} sx={{ cursor: isTruncated ? 'pointer' : 'default' }} onClick={handleBubble}>
            <Typography variant="body2" fontSize="14px">
                {displayMessage}
                {isExpanded && (
                    <Box
                        component="span"
                        sx={{
                            pl: 0.5,
                            fontWeight: 'bold',
                            textDecoration: 'underline',
                        }}
                    >
                        ...less
                    </Box>
                )}
                {isTruncated && !isExpanded && (
                    <Box
                        component="span"
                        sx={{
                            pl: 0.5,
                            fontWeight: 'bold',
                            textDecoration: 'underline',
                        }}
                    >
                        ...more
                    </Box>
                )}
            </Typography>
        </Stack>
    )
}

export default BubbleContent
